import type {
  BatchGroupInfo,
  CheckLane,
  PipelineGraphSkeleton,
} from "@/types/aicheck";
import {
  NODE_IDS,
  type PipelineEdge,
  type PipelineLane,
  type PipelineNode,
} from "./types";

function dedupeById<T extends { id: string }>(items: T[]): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const item of items) {
    if (seen.has(item.id)) continue;
    seen.add(item.id);
    out.push(item);
  }
  return out;
}

function makeNode(
  id: string,
  nodeType: string,
  label: string,
  lane: PipelineLane,
  extra?: Record<string, unknown>,
): PipelineNode {
  return {
    id,
    type: nodeType,
    position: { x: 0, y: 0 }, // dagre will fill this
    data: {
      nodeType,
      label,
      status: "planned",
      lane,
      ...extra,
    } as PipelineNode["data"],
  };
}

function makeEdge(source: string, target: string): PipelineEdge {
  return {
    id: `e-${source}-${target}`,
    source,
    target,
    type: "pipeline",
    data: { status: "planned" },
  };
}

export function buildGraphSkeleton(
  checkLaneMap: Record<string, CheckLane> | undefined,
  plannedChecks: string[] | undefined,
  batchGroups?: BatchGroupInfo[],
): { nodes: PipelineNode[]; edges: PipelineEdge[] } {
  const nodes: PipelineNode[] = [];
  const edges: PipelineEdge[] = [];

  // Deduplicate plannedChecks (it's append-only)
  const rawChecks = [...new Set(plannedChecks ?? [])];
  const laneMap = checkLaneMap ?? {};

  // Filter out executor-emitted batch display names (e.g. "Test Batch (batch 4)")
  // These are added by the batch-executor and should not create separate nodes.
  const batchDisplayPrefixes = (batchGroups ?? []).map(
    (bg) => `${bg.name} (batch `,
  );
  const allChecks = rawChecks.filter(
    (name) => !batchDisplayPrefixes.some((pfx) => name.startsWith(pfx)),
  );

  if (allChecks.length === 0) {
    // Minimal skeleton: fetch + consistency (consistency rules always run)
    nodes.push(makeNode(NODE_IDS.FETCH, "system", "Fetch Data", "pre-fork"));
    nodes.push(
      makeNode(
        NODE_IDS.CONSISTENCY_INDEPENDENT,
        "consistency",
        "Consistency Rules",
        "pre-fork",
        { ruleCount: 0, passedCount: 0, failedCount: 0, warningCount: 0 },
      ),
    );
    edges.push(makeEdge(NODE_IDS.FETCH, NODE_IDS.CONSISTENCY_INDEPENDENT));
    return { nodes: dedupeById(nodes), edges: dedupeById(edges) };
  }

  // Build batch membership: checkName → batchGroupId
  const checkToBatch = new Map<string, string>();
  const batchMap = new Map<string, BatchGroupInfo>();
  for (const bg of batchGroups ?? []) {
    batchMap.set(bg.id, bg);
    for (const name of bg.checks) {
      checkToBatch.set(name, bg.id);
    }
  }

  // Classify checks by lane, filtering out batch display names from executor
  // (they look like "Batch Name (batch N)" and are not in checkLaneMap)
  const independentChecks = allChecks.filter(
    (n) => laneMap[n] === "independent",
  );
  const dependentChecks = allChecks.filter((n) => laneMap[n] === "dependent");
  const afterAllChecks = allChecks.filter((n) => laneMap[n] === "afterAll");
  // Unknown lane checks go to independent
  const unknownChecks = allChecks.filter((n) => !laneMap[n]);

  const laneAChecks = [...independentChecks, ...unknownChecks];
  const laneBChecks = dependentChecks;

  // --- Pre-fork ---
  nodes.push(makeNode(NODE_IDS.FETCH, "system", "Fetch Data", "pre-fork"));
  nodes.push(
    makeNode(
      NODE_IDS.CONSISTENCY_INDEPENDENT,
      "consistency",
      "Consistency Rules",
      "pre-fork",
      { ruleCount: 0, passedCount: 0, failedCount: 0, warningCount: 0 },
    ),
  );
  edges.push(makeEdge(NODE_IDS.FETCH, NODE_IDS.CONSISTENCY_INDEPENDENT));

  // Helper: create nodes for a list of checks, grouping by batch
  function createCheckNodes(
    checks: string[],
    lane: PipelineLane,
    sourceNodeId: string,
  ): string[] {
    const nodeIds: string[] = [];
    const seenBatches = new Set<string>();

    for (const name of checks) {
      const batchId = checkToBatch.get(name);
      if (batchId && batchMap.has(batchId)) {
        // Part of a batch — create one node per batch group
        if (seenBatches.has(batchId)) continue;
        seenBatches.add(batchId);
        const bg = batchMap.get(batchId);
        if (!bg) continue;
        const nodeId = `ai-batch-${batchId}`;
        const memberChecks = bg.checks.filter((c) => checks.includes(c));
        nodes.push(
          makeNode(nodeId, "ai-check", bg.name, lane, {
            checks: memberChecks.map((c) => ({ name: c, status: "planned" })),
            batchSize: memberChecks.length,
          }),
        );
        edges.push(makeEdge(sourceNodeId, nodeId));
        nodeIds.push(nodeId);
      } else {
        // Standalone check
        const nodeId = `ai-${name}`;
        nodes.push(
          makeNode(nodeId, "ai-check", name, lane, {
            checks: [{ name, status: "planned" }],
            batchSize: 1,
          }),
        );
        edges.push(makeEdge(sourceNodeId, nodeId));
        nodeIds.push(nodeId);
      }
    }
    return nodeIds;
  }

  // --- Lane A: Independent AI checks (parallel fan-out from consistency) ---
  const laneANodeIds = createCheckNodes(
    laneAChecks,
    "lane-a",
    NODE_IDS.CONSISTENCY_INDEPENDENT,
  );

  // --- Lane B: Compaction -> Profile -> Profile Consistency -> Dependent checks ---
  nodes.push(
    makeNode(NODE_IDS.COMPACTION, "system", "Contact Compaction", "lane-b"),
  );
  edges.push(makeEdge(NODE_IDS.FETCH, NODE_IDS.COMPACTION));

  nodes.push(
    makeNode(NODE_IDS.PROFILE, "system", "Customer Profile", "lane-b"),
  );
  edges.push(makeEdge(NODE_IDS.COMPACTION, NODE_IDS.PROFILE));

  // Profile-dependent consistency rules
  nodes.push(
    makeNode(
      NODE_IDS.CONSISTENCY_PROFILE,
      "consistency",
      "Profile Consistency",
      "lane-b",
      { ruleCount: 0, passedCount: 0, failedCount: 0, warningCount: 0 },
    ),
  );
  edges.push(makeEdge(NODE_IDS.PROFILE, NODE_IDS.CONSISTENCY_PROFILE));

  // Dependent AI checks (parallel fan-out from consistency-profile)
  const laneBNodeIds = createCheckNodes(
    laneBChecks,
    "lane-b",
    NODE_IDS.CONSISTENCY_PROFILE,
  );

  // --- AfterAll checks (sequential) ---
  // Collect all terminal node IDs from lane-a and lane-b
  const terminalNodeIds: string[] = [];
  if (laneANodeIds.length > 0) {
    terminalNodeIds.push(...laneANodeIds);
  }
  if (laneBNodeIds.length > 0) {
    terminalNodeIds.push(...laneBNodeIds);
  } else {
    terminalNodeIds.push(NODE_IDS.CONSISTENCY_PROFILE);
  }
  // If no lane-a checks either, consistency is the only terminal
  if (laneANodeIds.length === 0 && laneBNodeIds.length === 0) {
    // Already has CONSISTENCY_PROFILE from above
  }

  if (afterAllChecks.length > 0) {
    // First afterAll check receives edges from all terminal nodes
    const firstAfterAllId = `summary-${afterAllChecks[0]}`;
    nodes.push(
      makeNode(firstAfterAllId, "summary", afterAllChecks[0], "post-join", {
        checks: [{ name: afterAllChecks[0], status: "planned" }],
        batchSize: 1,
      }),
    );
    for (const id of terminalNodeIds) {
      edges.push(makeEdge(id, firstAfterAllId));
    }

    // Remaining afterAll checks chain sequentially
    let prevAfterAll = firstAfterAllId;
    for (let i = 1; i < afterAllChecks.length; i++) {
      const name = afterAllChecks[i];
      const nodeId = `summary-${name}`;
      nodes.push(
        makeNode(nodeId, "summary", name, "post-join", {
          checks: [{ name, status: "planned" }],
          batchSize: 1,
        }),
      );
      edges.push(makeEdge(prevAfterAll, nodeId));
      prevAfterAll = nodeId;
    }
  }

  return { nodes: dedupeById(nodes), edges: dedupeById(edges) };
}

export function buildGraphFromServerSkeleton(skeleton: PipelineGraphSkeleton): {
  nodes: PipelineNode[];
  edges: PipelineEdge[];
} {
  const nodes: PipelineNode[] = skeleton.nodes.map((n) =>
    makeNode(
      n.id,
      n.nodeType,
      n.label,
      n.lane,
      n.nodeType === "ai-check" || n.nodeType === "summary"
        ? {
            checks: (n.checks ?? []).map((name) => ({
              name,
              status: "planned",
            })),
            batchSize: n.batchSize ?? n.checks?.length ?? 1,
          }
        : n.nodeType === "consistency"
          ? {
              ruleCount: n.ruleCount ?? 0,
              passedCount: n.passedCount ?? 0,
              failedCount: n.failedCount ?? 0,
              warningCount: n.warningCount ?? 0,
            }
          : undefined,
    ),
  );
  const edges: PipelineEdge[] = skeleton.edges.map((e) => ({
    id: e.id,
    source: e.source,
    target: e.target,
    type: "pipeline",
    data: { status: "planned" },
  }));
  return { nodes: dedupeById(nodes), edges: dedupeById(edges) };
}
